import {createLogger} from "@gongt/ts-stl-library/log/debug";
import {LOG_LEVEL} from "@gongt/ts-stl-library/log/levels";
import {ICacheClassPromise} from "@gongt/ts-stl-server/database/cache";
import {Request} from "express-serve-static-core";
import {IUserDataNormalizedClient, IUserProfile, IUserProfileBase} from "../bridge/define";
import {UserCenter} from "../includes/user-center";
import {UserCenterApi} from "../includes/user-center-api";
import {UserError} from "../includes/user-error";
import {UserInstance} from "../includes/user-instance";
import {tryGetTokenFromRequest} from "./try-get-token-from-request";

const debug = createLogger(LOG_LEVEL.INFO, 'login');

export class UserSystem<IProfile extends IUserProfileBase> {
	private api: UserCenterApi<IProfile>;
	private cache: ICacheClassPromise;
	
	constructor(private uc: UserCenter<IProfile>) {
		this.api = uc.api;
		this.cache = uc.cache;
	}
	
	/** @internal */
	createEmpty(req?: Request) {
		let token;
		if (req) {
			token = tryGetTokenFromRequest(req).token;
		}
		return new UserInstance<IProfile>(<any>{token: token}, this.uc);
	}
	
	/** 根据email获取用户id */
	async mapEmailToId(email: string) {
		let id: string = await this.cache.get(['login-to-id', email]);
		if (!id) {
			const data = await this.api.getUserProfile({email});
			id = data.user.id;
		}
		
		this.cache.set(['login-to-id', email], id, 3600).catch(this.cache.ignoreError);
		return id;
	}
	
	async profileByEmail(email): Promise<UserInstance<IProfile>> {
		return this.profile(await this.mapEmailToId(email));
	}
	
	/** 根据id获取用户信息 */
	async profile(id): Promise<UserInstance<IProfile>> {
		const found = await this.cache.get(['profile', id]);
		let user: IUserDataNormalizedClient<IProfile>;
		if (found) {
			user = found;
		} else {
			user = (await this.api.getUserProfile({userId: id})).user;
		}
		
		this.cache.set(['profile', id], user, 60).catch(this.cache.ignoreError);
		return new UserInstance<IProfile>(user, this.uc);
	}
	
	/**
	 * @private
	 */
	async login(login: string|Request): Promise<UserInstance<IProfile>>;
	/** 手动登陆 */
	async login(login: string, password: string): Promise<UserInstance<IProfile>>;
	async login(login: string|Request, password?: string): Promise<UserInstance<IProfile>> {
		let safe = true;
		if (typeof login !== 'string') {
			const ret = tryGetTokenFromRequest(login);
			login = ret.token;
			safe = ret.safe;
		}
		
		if (!login) {
			throw new UserError('no token or login');
		}
		debug('do_login: %s | %s', login, password);
		
		let ret, user: IUserDataNormalizedClient<IProfile>;
		if (password) {
			debug('  login with login & password:');
			ret = await this.api.userLogin({ // no any way cache??
				login: login,
				password: password,
			});
		} else {
			debug('  login with token:');
			if (safe) {
				const user = await this.cache.get(['token', login]);
				if (user) {
					debug('    found user in cache');
					return new UserInstance<IProfile>(user, this.uc);
				}
			} // TODO: else save to safe store (signed cookie)
			ret = await this.api.getCurrentUser({token: login});
		}
		debug('    fetch user ok');
		user = ret.user;
		user.userCenterLoginToken = ret.token;
		
		if (password) {
			this.cache.set(['login-to-token', login], ret.token, 300).catch(this.cache.ignoreError);
		}
		
		this.cache.set(['token', ret.token], user, 300).catch(this.cache.ignoreError);
		
		return new UserInstance<IProfile>(user, this.uc);
	}
	
	async updateProfile(token: string, newProfile: Partial<IProfile>): Promise<any> {
		const {avatarId, ...props} = newProfile as any;
		const ret = {avatar: false, info: false};
		if (avatarId) {
			await this.api.updateAvatar({
				token: token,
				fileId: avatarId,
			});
			ret.avatar = true;
		}
		if (Object.keys(props).length > 0) {
			await this.api.updateProfile({
				token: token,
				newProfile: props,
			});
			ret.info = true;
		}
		await this.uc.updateCache(token).catch(this.uc.cache.ignoreError);
		return ret;
	}
}
